import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField,
  Typography, InputAdornment, Stack, ToggleButton, ToggleButtonGroup, FormControlLabel, Switch,
} from '@mui/material';
import {
  PAYMENT_METHOD, calcTaxCents, centsToDollars, dollarsToCents, formatPrice, layawayApi, toDate, fmtDate,
  isCustody, firearmCount, monthsOwed, canExtendContract,
} from '../../../utils/layaways';

// Registrar un pago hecho EN TIENDA (efectivo, ATH, tarjeta en el terminal).
// Reglas del contrato (nota de Tuto 2026-10-08):
//   · Con balance pendiente: abono, o EXTENSIÓN de contrato (60 días / $30).
//   · Arma saldada o custodia: ALMACENAJE ($20/mes por arma). Si está vencido,
//     se sugieren todos los meses que debe.
export default function LayawayPaymentDialog({ open, onClose, layaway, settings, onSaved, initialKind = 'payment' }) {
  const [kind, setKind] = useState(initialKind);
  const [amount, setAmount] = useState('');
  const [months, setMonths] = useState(1);
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);
  const [customStorage, setCustomStorage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const balance = Number(layaway && layaway.balanceCents) || 0;
  const cust = isCustody(layaway);
  const paidOff = !cust && Boolean(layaway) && balance <= 0;
  // Almacenaje solo aplica a custodia o arma saldada; extensión solo con balance.
  const storageAllowed = cust || paidOff;
  const extensionAllowed = !cust && !paidOff;
  const owed = layaway ? monthsOwed(layaway, settings) : 0;
  const extOk = canExtendContract(layaway, settings);

  useEffect(() => {
    if (!open) return;
    let k = initialKind;
    if (k === 'storage' && !storageAllowed) k = 'extension';
    if (k === 'extension' && !extensionAllowed) k = 'storage';
    if (k === 'payment' && (cust || paidOff)) k = 'storage';
    setKind(k); setAmount(''); setMonths(Math.max(1, owed)); setMethod('cash'); setNote(''); setSendReceipt(Boolean(layaway && layaway.memberEmail));
    setCustomStorage(false); setSaving(false); setError('');
  }, [open, initialKind, layaway, settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const units = firearmCount(layaway);
  const feeCents = (Number(settings.storageFeeCents) || 0) * units;
  const storageSubtotal = customStorage ? dollarsToCents(amount) : feeCents * months;
  const storageTax = settings.storageFeeTaxable ? calcTaxCents(storageSubtotal) : 0;
  const extFee = Number(settings.extensionFeeCents) || 0;
  const extTax = settings.extensionFeeTaxable !== false ? calcTaxCents(extFee) : 0;
  const extDays = Number(settings.extensionDays) || 60;
  const amountCents = dollarsToCents(amount);
  const newDue = useMemo(() => {
    const d = toDate(layaway && layaway.dueDate);
    if (!d) return null;
    const n = new Date(d);
    n.setDate(n.getDate() + (kind === 'extension' ? extDays : months * (Number(settings.storageMonthDays) || 30)));
    return n;
  }, [layaway, months, kind, extDays, settings.storageMonthDays]);

  const valid = kind === 'storage'
    ? (customStorage ? amountCents >= 0 : feeCents > 0)
    : kind === 'extension' ? extFee > 0 : (amountCents > 0 && amountCents <= balance);

  const save = async () => {
    if (!valid) { setError(kind === 'payment' ? `El abono debe ser entre $0.01 y ${formatPrice(balance)}` : 'Revisa el monto'); return; }
    setSaving(true); setError('');
    try {
      const res = await layawayApi.recordPayment({
        layawayId: layaway.id,
        kind,
        amountCents: kind === 'storage' ? (customStorage ? amountCents : 0) : kind === 'extension' ? 0 : amountCents,
        months: kind === 'storage' ? months : 0,
        method, note, sendReceipt,
      });
      if (onSaved) onSaved(res.layaway);
      onClose();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  if (!layaway) return null;
  const total = kind === 'storage' ? storageSubtotal + storageTax : kind === 'extension' ? extFee + extTax : amountCents;
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Registrar pago en tienda</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {cust && (
            <Alert severity="info">Custodia de {units} arma{units === 1 ? '' : 's'}: el almacenaje es {formatPrice(settings.storageFeeCents)} por arma al mes, todas en un solo pago.</Alert>
          )}
          {paidOff && (
            <Alert severity={owed > 0 ? 'warning' : 'info'}>Arma saldada. {owed > 0 ? `Pasó la fecha límite sin recogerla: debe ${owed} mes${owed === 1 ? '' : 'es'} de almacenaje (${formatPrice((feeCents + (settings.storageFeeTaxable ? calcTaxCents(feeCents) : 0)) * owed)}).` : 'Según el contrato, pasada la fecha límite aplica almacenaje mensual.'}</Alert>
          )}
          {!cust && !paidOff && (
            <ToggleButtonGroup exclusive value={kind} onChange={(e, v) => v && setKind(v)} fullWidth size="small">
              <ToggleButton value="payment">Abono al balance</ToggleButton>
              <ToggleButton value="extension">Extensión de contrato ({extDays} d · {formatPrice(extFee)})</ToggleButton>
            </ToggleButtonGroup>
          )}

          {kind === 'payment' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Balance actual: <strong>{formatPrice(balance)}</strong></Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField autoFocus fullWidth label="Cantidad abonada" value={amount} onChange={(e) => setAmount(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" sx={{ height: 56 }} onClick={() => setAmount(centsToDollars(balance))}>Saldar todo ({formatPrice(balance)})</Button>
              </Grid>
              {amountCents > 0 && amountCents <= balance && (
                <Grid item xs={12}>
                  <Alert severity={balance - amountCents === 0 ? 'success' : 'info'}>
                    {balance - amountCents === 0 ? 'Con este pago el arma queda SALDADA.' : `Balance después del pago: ${formatPrice(balance - amountCents)}`}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}

          {kind === 'extension' && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Cláusula 8: si el cliente no ha saldado, puede extender el contrato <b>{extDays} días</b> por <b>{formatPrice(extFee)}</b>{extTax > 0 ? ` + IVU ${formatPrice(extTax)}` : ''}.
                Fecha límite actual: <strong>{fmtDate(layaway.dueDate)}</strong>.
              </Typography>
              {!extOk && <Alert severity="warning">Este layaway ya usó {layaway.extensionCount || 0} extensión(es); el contrato permite {settings.extensionMaxCount}. Puedes registrarla igual como excepción del staff.</Alert>}
              <Alert severity="info">Total a cobrar: <strong>{formatPrice(extFee + extTax)}</strong>. Nueva fecha límite: <strong>{newDue ? fmtDate(newDue) : '—'}</strong></Alert>
            </Stack>
          )}

          {kind === 'storage' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {cust ? 'Pagado hasta' : 'Fecha límite actual'}: <strong>{fmtDate(layaway.dueDate)}</strong>. Cada mes de almacenaje la extiende {settings.storageMonthDays || 30} días.
                  {owed > 0 ? ` Debe ${owed} mes${owed === 1 ? '' : 'es'}.` : ''}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth label="Meses" value={months} onChange={(e) => setMonths(Number(e.target.value))} helperText={owed > 0 && months < owed ? `El cliente debe ${owed}; menos meses es una excepción del staff` : ' '}>
                  {Array.from({ length: Math.max(12, owed) }, (_, i) => i + 1).map((m) => <MenuItem key={m} value={m}>{m} mes{m > 1 ? 'es' : ''}{m === owed ? ' (lo que debe)' : ''}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Monto" value={customStorage ? amount : centsToDollars(storageSubtotal)} onChange={(e) => { setCustomStorage(true); setAmount(e.target.value); }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} helperText={customStorage ? 'Monto manual (cortesía/ajuste)' : `${formatPrice(settings.storageFeeCents)}${units > 1 ? ` × ${units} armas` : ''} × ${months}`} />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  Total a cobrar: <strong>{formatPrice(storageSubtotal + storageTax)}</strong>{storageTax > 0 ? ` (incluye IVU ${formatPrice(storageTax)})` : ''}. {cust ? 'Pagado hasta' : 'Nueva fecha límite'}: <strong>{newDue ? fmtDate(newDue) : '—'}</strong>
                </Alert>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select fullWidth label="Método de pago" value={method} onChange={(e) => setMethod(e.target.value)}>
                {Object.entries(PAYMENT_METHOD).filter(([k]) => k !== 'card_online').map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
            </Grid>
          </Grid>
          <FormControlLabel control={<Switch checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} disabled={!layaway.memberEmail} />} label={layaway.memberEmail ? `Enviar recibo a ${layaway.memberEmail}` : 'Sin email: no se envía recibo'} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving || !valid}>{saving ? 'Guardando…' : `Registrar ${formatPrice(total)}`}</Button>
      </DialogActions>
    </Dialog>
  );
}

LayawayPaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  layaway: PropTypes.object,
  settings: PropTypes.object.isRequired,
  onSaved: PropTypes.func,
  initialKind: PropTypes.string,
};
